// Bulletproof "am I on the latest bundle?" check.
//
// PWA / mobile-Safari caches are notorious for serving stale HTML +
// stale JS bundles even after deploys, and the browser's voluntary
// service-worker update polling is unreliable (sometimes fires after
// 60s, sometimes 24h, sometimes never depending on how the PWA was
// launched). This check sidesteps all of that:
//
//   1. The bundled JS knows its own bundle URL — Vite content-hashes
//      the filename, so `index-CDPYqmTP.js` is a unique build identity.
//   2. The server exposes /api/version which returns the bundle hash
//      of the index.html the server is currently serving.
//   3. If they differ, this JS is STALE. We:
//        - Unregister every service worker
//        - Clear every cache via caches.delete()
//        - location.reload() — comes back fresh, picks up new HTML +
//          new bundle hash, the new JS matches the server, no reload
//          loop.
//
// Reload-loop guard: a localStorage timestamp records when we last
// triggered a reload. If we'd reload again within 30s, we skip and log
// a warning instead — protects users from a genuine misconfiguration
// (e.g. /api/version returning a different hash than the deployed
// index.html) from spinning forever.

import { showUpdatingOverlay } from "./updating-overlay";

const LAST_RELOAD_KEY = "cornholio.version-check.lastReloadAt";
const RELOAD_COOLDOWN_MS = 30_000;
// How long to wait between checks while the app is open. 60s = a deploy
// rolls out within a minute of the user being active.
const CHECK_INTERVAL_MS = 60_000;

function extractBundleHashFromUrl(url: string): string | null {
  // /assets/index-CDPYqmTP.js → CDPYqmTP
  const m = url.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js(?:\?.*)?$/);
  return m ? m[1]! : null;
}

function getOwnBundleHash(): string | null {
  // The currently-loaded module script's src points to our own bundle.
  // We look at every script tag because Vite's HTML emit puts the entry
  // bundle as a single script[type=module] tag; matching on the
  // filename pattern handles any source-order variation.
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[src*="/assets/index-"]',
  );
  for (const s of Array.from(scripts)) {
    const hash = extractBundleHashFromUrl(s.src);
    if (hash) return hash;
  }
  return null;
}

async function fetchServerBundleHash(): Promise<string | null> {
  try {
    const res = await fetch("/api/version", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { hash?: string };
    return typeof data.hash === "string" ? data.hash : null;
  } catch {
    return null;
  }
}

async function clearAllCaches(): Promise<void> {
  try {
    if (!("caches" in window)) return;
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch {
    // Non-fatal: reload alone is often enough.
  }
}

async function unregisterAllServiceWorkers(): Promise<void> {
  try {
    if (!("serviceWorker" in navigator)) return;
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  } catch {
    // Non-fatal.
  }
}

function recentlyReloaded(): boolean {
  try {
    const raw = window.localStorage.getItem(LAST_RELOAD_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markReloadTriggered(): void {
  try {
    window.localStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

// One-shot: fetch the server's expected bundle hash, compare to ours,
// nuke caches + reload if stale. Safe to call on every page load — a
// matching hash is a cheap no-op.
export async function checkBundleVersionOnce(): Promise<void> {
  if (typeof window === "undefined") return;
  const ownHash = getOwnBundleHash();
  if (!ownHash) return; // dev mode or HTML emitted differently
  const serverHash = await fetchServerBundleHash();
  if (!serverHash) return;
  if (ownHash === serverHash) return;
  // Stale. Reload defensively (not more than once every 30s).
  if (recentlyReloaded()) {
    // eslint-disable-next-line no-console
    console.warn(
      "[version-check] hash mismatch but cooldown active — skipping reload",
      { ownHash, serverHash },
    );
    return;
  }
  // eslint-disable-next-line no-console
  console.warn("[version-check] stale bundle detected — forcing reload", {
    ownHash,
    serverHash,
  });
  markReloadTriggered();
  // Tell the user WHY the app is about to go blank and reload. Clearing every
  // cache below means the next load re-fetches everything, so this is the
  // slowest reload we ever do — exactly the one that needs an explanation.
  showUpdatingOverlay();
  await unregisterAllServiceWorkers();
  await clearAllCaches();
  window.location.reload();
}

// Boot-time installation: run the check now, then on a recurring
// interval so a long-lived session catches deploys without the user
// having to navigate. Also fires on tab focus + network online events
// — those are the moments where the user is most likely to want
// freshness (just resumed the app, or just regained connectivity).
export function installVersionCheck(): void {
  if (typeof window === "undefined") return;
  // Initial check, deferred so it doesn't compete with SPA boot.
  setTimeout(() => {
    void checkBundleVersionOnce();
  }, 2000);
  setInterval(() => {
    void checkBundleVersionOnce();
  }, CHECK_INTERVAL_MS);
  window.addEventListener("focus", () => {
    void checkBundleVersionOnce();
  });
  window.addEventListener("online", () => {
    void checkBundleVersionOnce();
  });
}
